import React from 'react';
import { Paper, Box, Typography, Badge, Button } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import TaskCard from './TaskCard';

/**
 * Eine Board-Spalte.
 *
 * `totalCount` wird nur bei aktiver Suche gesetzt: die Spalte zeigt dann „3/27"
 * und bleibt auch ohne Treffer stehen (nur gedämpft). Würde sie ausgeblendet,
 * spränge das Layout und genau die räumliche Orientierung ginge verloren,
 * wegen der das Board überhaupt in-place gefiltert wird.
 */
function KanbanColumn({
  status, tasks, onTaskClick, hiddenDoneCount = 0, onShowAllDone,
  totalCount = null, sortableDisabled = false,
}) {
  const { setNodeRef } = useDroppable({
    id: status.id,
  });

  const filtering = totalCount !== null;
  const dimmed = filtering && tasks.length === 0;

  return (
    <Paper
      sx={{
        flex: '1 1 300px',
        minWidth: 280,
        height: '100%',
        bgcolor: 'background.default',
        borderTop: 3,
        borderColor: status.color,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: dimmed ? 0.45 : 1,
        transition: 'opacity 150ms',
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {status.name}
        </Typography>
        {filtering ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {tasks.length} / {totalCount}
          </Typography>
        ) : (
          <Badge badgeContent={tasks.length} color="primary" />
        )}
      </Box>

      {/* Tasks List */}
      <Box
        ref={setNodeRef}
        sx={{
          flexGrow: 1,
          overflowY: 'scroll',
          overflowX: 'hidden',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: 0,
          '&::-webkit-scrollbar': {
            width: '8px',
            display: 'block',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#555',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#888 #f1f1f1',
        }}
      >
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={onTaskClick}
              sortableDisabled={sortableDisabled}
            />
          ))}
        </SortableContext>

        {tasks.length === 0 && hiddenDoneCount === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              {filtering ? 'Keine Treffer' : 'Keine Tasks'}
            </Typography>
          </Box>
        )}

        {hiddenDoneCount > 0 && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={onShowAllDone}
            sx={{ mt: 1, color: 'text.secondary', textTransform: 'none' }}
          >
            {hiddenDoneCount} ältere anzeigen
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default KanbanColumn;
